/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Create ads", description = "Start a chat, choose a starting point — a brief, product, existing ad, competitor, or trend — generate ad concepts, and add more sizes and motion.", lastUpdatedDate = new Date(1792368000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Create ads",
        description: "Start a chat, choose a starting point — a brief, product, existing ad, competitor, or trend — generate ad concepts, and add more sizes and motion.",
        lastUpdatedDate: "Mon Oct 19 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "How ads work in Ad Studio",
          "depth": 0,
          "id": "how-ads-work-in-ad-studio"
        }, {
          "value": "Start a chat",
          "depth": 0,
          "id": "start-a-chat"
        }, {
          "value": "Create ads",
          "depth": 0,
          "id": "create-ads"
        }, {
          "value": "Select a brand and style",
          "depth": 1,
          "id": "select-a-brand-and-style"
        }, {
          "value": "Choose a starting point",
          "depth": 1,
          "id": "choose-a-starting-point"
        }, {
          "value": "Upload a brief",
          "depth": 1,
          "id": "upload-a-brief"
        }, {
          "value": "Choose a product",
          "depth": 1,
          "id": "choose-a-product"
        }, {
          "value": "Upload an ad and create variants",
          "depth": 1,
          "id": "upload-an-ad-and-create-variants"
        }, {
          "value": "Riff on a competitor",
          "depth": 1,
          "id": "riff-on-a-competitor"
        }, {
          "value": "Ride a new trend",
          "depth": 1,
          "id": "ride-a-new-trend"
        }, {
          "value": "Start from an idea",
          "depth": 1,
          "id": "start-from-an-idea"
        }, {
          "value": "Create more formats",
          "depth": 0,
          "id": "create-more-formats"
        }, {
          "value": "Generate additional sizes",
          "depth": 1,
          "id": "generate-additional-sizes"
        }, {
          "value": "Animate a static ad",
          "depth": 1,
          "id": "animate-a-static-ad"
        }, {
          "value": "Create motion ads from scratch",
          "depth": 1,
          "id": "create-motion-ads-from-scratch"
        }, {
          "value": "Turn a long video into multiple ads",
          "depth": 1,
          "id": "turn-a-long-video-into-multiple-ads"
        }, {
          "value": "Organize your ads",
          "depth": 0,
          "id": "organize-your-ads"
        }, {
          "value": "What's next",
          "depth": 0,
          "id": "whats-next"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      ol: "ol",
      li: "li",
      img: "img",
      h3: "h3",
      ul: "ul"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Marketers, creative teams"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsx(_components.td, {
              children: _jsxs("ul", {
                children: [_jsx("li", {
                  children: _jsx(_components.a, {
                    href: "/ad-studio/overview#get-started",
                    children: "Ad Studio setup complete"
                  })
                }), _jsxs("li", {
                  children: ["At least one connected ad platform in Context Hub's ", _jsx(_components.a, {
                    href: "/context-hub/connections#connect-advertising-and-lifecycle-platforms",
                    children: "Connected data"
                  })]
                })]
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Ads are the creative output of Ad Studio. Start a chat, choose a starting point, and prompt the agent to generate concepts you can refine and launch."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "how-ads-work-in-ad-studio",
        children: "How ads work in Ad Studio"
      }), "\n", _jsx(_components.p, {
        children: "Every ad follows the same six steps:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Start or open a chat"
          }), " for the campaign you're working on."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Choose a starting point (optional)"
          }), " — a brief, a product, an existing ad, a competitor ad, or a trend. You can skip this and just describe what you want."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Prompt the agent"
          }), " to describe what you want."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Review the generated concepts"
          }), " and pick the ones worth keeping."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Refine"
          }), " them in the chat or the visual editor."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Launch"
          }), " to an ad platform, or open the ad in Figma."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["This page covers the first four steps — starting a chat and generating concepts. ", _jsx(_components.a, {
          href: "/ad-studio/refine",
          children: "Refining"
        }), ", ", _jsx(_components.a, {
          href: "/ad-studio/launch",
          children: "launching"
        }), ", and ", _jsx(_components.a, {
          href: "/ad-studio/figma",
          children: "handing off to Figma"
        }), " each have their own page. For prompt patterns and copy-paste templates, see the ", _jsx(_components.a, {
          href: "/ad-studio/prompting-guide",
          children: "Prompting guide"
        }), "."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "start-a-chat",
        children: "Start a chat"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ad-studio/ads/as-ads-new-chat.png",
          alt: "Ad Studio Ads sidebar with New chat selected and the create screen open"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Each chat is where you create ads for a campaign or creative initiative. Select ", _jsx(_components.strong, {
          children: "New chat"
        }), " in the sidebar, describe what you want, and the agent generates concepts in that chat. The chat keeps its conversation history and the ads generated in it together."]
      }), "\n", _jsxs(_components.p, {
        children: ["To give the agent reference material for a single request, attach files directly in the chat. For material that should inform every ad you make — brand guidelines, logos, fonts, and reusable assets — add it as durable context in ", _jsx(_components.a, {
          href: "/context-hub/creative",
          children: "Context Hub"
        }), " and ", _jsx(_components.a, {
          href: "/context-hub/assets",
          children: "Assets"
        }), " instead of re-attaching it each time."]
      }), "\n", _jsxs(_components.p, {
        children: ["Your ads aren't locked inside one chat. ", _jsx(_components.a, {
          href: "#organize-your-ads",
          children: "Folders and the sidebar collections"
        }), " let you find and group any ad regardless of which chat made it."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "create-ads",
        children: "Create ads"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ad-studio/ads/as-ads-create-new.png",
          alt: "The create screen with the prompt input and five starting-point tiles"
        })
      }), "\n", _jsx(_components.p, {
        children: "Every ad starts in a chat. Set your brand and style, then describe what you want or pick a starting point to work from."
      }), "\n", _jsx(_components.h3, {
        id: "select-a-brand-and-style",
        children: "Select a brand and style"
      }), "\n", _jsx(_components.p, {
        children: "Set two controls in the chat before you generate, and change them on any round:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Brand"
          }), " — Which ", _jsx(_components.a, {
            href: "/context-hub/brands",
            children: "brand profile"
          }), " the agent draws from: its business context, guidelines, logos, and fonts. You never have to describe your brand in a prompt, and your default brand is selected to start."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Style"
          }), " — The visual direction. Leave it on ", _jsx(_components.strong, {
            children: "Auto"
          }), " to let the agent decide, pick a saved ", _jsx(_components.a, {
            href: "/context-hub/creative#save-visual-styles",
            children: "style"
          }), " to lock a defined look, or choose ", _jsx(_components.strong, {
            children: "No style"
          }), ". Styles are filtered to the selected brand, so switching brands resets the style to ", _jsx(_components.strong, {
            children: "Auto"
          }), "."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Your uploaded ", _jsx(_components.a, {
          href: "/context-hub/assets",
          children: "assets"
        }), ", synced ads, and ", _jsx(_components.a, {
          href: "/context-hub/assets#connect-a-product-catalog",
          children: "product catalog"
        }), " are also available for the agent to draw on when you reference them."]
      }), "\n", _jsx(_components.h3, {
        id: "choose-a-starting-point",
        children: "Choose a starting point"
      }), "\n", _jsx(_components.p, {
        children: "Describe what you want in the text input, and Ad Studio combines your prompt with your brand, guidelines, assets, and selected style to generate concepts."
      }), "\n", _jsx(_components.p, {
        children: "For a more guided start, select one of the five creation modes. Use this table to pick one:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Start with"
            }), _jsx(_components.th, {
              children: "When"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#upload-a-brief",
                children: "Upload a brief"
              })
            }), _jsx(_components.td, {
              children: "You already have a campaign brief, one-pager, or written requirements."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#choose-a-product",
                children: "Choose a product"
              })
            }), _jsx(_components.td, {
              children: "You want ads built from real product images, descriptions, and pricing."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#upload-an-ad-and-create-variants",
                children: "Create variants"
              })
            }), _jsx(_components.td, {
              children: "You have an ad that performs well and want alternatives to test against it."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#riff-on-a-competitor",
                children: "Riff on a competitor"
              })
            }), _jsx(_components.td, {
              children: "You've seen a competitor ad and want your own version of the idea."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#ride-a-new-trend",
                children: "Ride a new trend"
              })
            }), _jsx(_components.td, {
              children: "A cultural moment is happening now and you need creative quickly."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "upload-a-brief",
        children: "Upload a brief"
      }), "\n", _jsx(_components.p, {
        children: "Use this when you already have a campaign brief or creative brief. It's the most open-ended starting point and the most common way to create ads."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Ad Studio > Ads"
          }), " and select ", _jsx(_components.strong, {
            children: "New chat"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Upload a brief"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Upload the document, or describe the campaign in the text input."
        }), "\n", _jsx(_components.li, {
          children: "Add any extra instructions, such as how many ads you want or which part of the brief matters most."
        }), "\n", _jsx(_components.li, {
          children: "Review the generated ads."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Ad Studio pulls the audience, message, and selling points from the brief and combines them with your brand context and uploaded assets."
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "Name your target audience, key differentiators, and the tone you want. Specific inputs produce more relevant creatives on the first pass."
        })
      }), "\n", _jsx(_components.h3, {
        id: "choose-a-product",
        children: "Choose a product"
      }), "\n", _jsxs(_components.p, {
        children: ["Use this when you want ads built around a specific product. Ad Studio pulls product images, descriptions, and pricing from your ", _jsx(_components.a, {
          href: "/context-hub/assets#connect-a-product-catalog",
          children: "product catalog"
        }), "."]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Ad Studio > Ads"
          }), " and select ", _jsx(_components.strong, {
            children: "New chat"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Choose a product"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Search or browse your catalog and select one or more products."
        }), "\n", _jsx(_components.li, {
          children: "Review the generated ads."
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["This mode needs a configured product catalog. See ", _jsx(_components.a, {
            href: "/context-hub/assets#connect-a-product-catalog",
            children: "Product catalog"
          }), " for setup."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "upload-an-ad-and-create-variants",
        children: "Upload an ad and create variants"
      }), "\n", _jsx(_components.p, {
        children: "Use this when you have an ad worth testing against — usually your top performer. Ad Studio holds the core idea and varies the parts you name."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Ad Studio > Ads"
          }), " and select ", _jsx(_components.strong, {
            children: "New chat"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Upload ad and create variants"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Upload an ad, or select one from your library."
        }), "\n", _jsx(_components.li, {
          children: "Name what should vary: headline, imagery, format, CTA, or a combination."
        }), "\n", _jsx(_components.li, {
          children: "Review the generated variants."
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "riff-on-a-competitor",
        children: "Riff on a competitor"
      }), "\n", _jsx(_components.p, {
        children: "Upload a competitor ad to generate new concepts inspired by its layout, messaging, or visual direction. Ad Studio applies your own brand context instead of copying the original creative."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Ad Studio > Ads"
          }), " and select ", _jsx(_components.strong, {
            children: "New chat"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Riff on a competitor"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Upload or describe the competitor ad."
        }), "\n", _jsx(_components.li, {
          children: "Review the generated ads."
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["You can also browse competitor ads in the Competitors tab of ", _jsx(_components.a, {
          href: "/ad-studio/insights#analyze-competitor-ads",
          children: "Insights"
        }), " and start from there."]
      }), "\n", _jsx(_components.h3, {
        id: "ride-a-new-trend",
        children: "Ride a new trend"
      }), "\n", _jsx(_components.p, {
        children: "Use this when a cultural moment, trending topic, or timely event is happening and speed matters."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Ad Studio > Ads"
          }), " and select ", _jsx(_components.strong, {
            children: "New chat"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Ride a new trend"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Describe the trend or moment."
        }), "\n", _jsx(_components.li, {
          children: "Review the generated ads."
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "start-from-an-idea",
        children: "Start from an idea"
      }), "\n", _jsxs(_components.p, {
        children: ["Ad Studio generates a weekly feed of ad ideas drawn from your performance data, competitors, and cultural signals. Open ", _jsx(_components.strong, {
          children: "Ideas"
        }), " in the sidebar to browse the feed, or select ", _jsx(_components.strong, {
          children: "Generate ideas now"
        }), " to run it on demand. New ideas are checked against the last 30 days, so the same concept doesn't come back twice."]
      }), "\n", _jsxs(_components.p, {
        children: ["Each idea has a name, a short rationale, the evidence behind it, and source tags that show where it came from, such as ", _jsx(_components.strong, {
          children: "Competitors"
        }), " or ", _jsx(_components.strong, {
          children: "Customer voice"
        }), ". Every idea ships with editable mockups, so you see the concept instead of only reading it."]
      }), "\n", _jsxs(_components.p, {
        children: ["When an idea is worth pursuing, select ", _jsx(_components.strong, {
          children: "Use idea"
        }), ". Ad Studio opens a new chat, writes a brief from the idea, and generates the ads for you to refine. Select ", _jsx(_components.strong, {
          children: "Discard"
        }), " to remove one that isn't relevant."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "create-more-formats",
        children: "Create more formats"
      }), "\n", _jsx(_components.h3, {
        id: "generate-additional-sizes",
        children: "Generate additional sizes"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ad-studio/ads/as-ads-generate-size-variants.png",
          alt: "Size variant picker showing available aspect ratios"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Select ", _jsx(_components.strong, {
          children: "+"
        }), " in the ", _jsx(_components.strong, {
          children: "Size variants"
        }), " panel to add aspect ratios. The defaults are 16:9, 3:2, 4:3, 5:4, 1:1, 4:5, 3:4, 2:3, and 9:16, and your list also includes any custom channels and sizes configured for your workspace. Select ", _jsx(_components.strong, {
          children: "Create all sizes"
        }), " to generate every format at once."]
      }), "\n", _jsx(_components.p, {
        children: "Size variants appear beside the primary creative so you can review them together."
      }), "\n", _jsx(_components.h3, {
        id: "animate-a-static-ad",
        children: "Animate a static ad"
      }), "\n", _jsx(_components.p, {
        children: "Select one or more static ads and prompt the agent to animate them. Your typography, logo, and layout stay as designed, so the agent only solves for movement."
      }), "\n", _jsx(_components.p, {
        children: "For example: \"Animate these ads, make them cinematic,\" or \"Turn this into a motion ad with moving text and a product reveal.\""
      }), "\n", _jsx(_components.h3, {
        id: "create-motion-ads-from-scratch",
        children: "Create motion ads from scratch"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ad-studio/ads/as-ads-motion-editor.png",
          alt: "Motion ad editor with the video timeline, scene prompt, and composition controls"
        })
      }), "\n", _jsx(_components.p, {
        children: "Prompt the agent for motion video directly from a brief, the same way you would for statics. For example: \"Create 5 new motion video ads based on these concepts.\""
      }), "\n", _jsx(_components.p, {
        children: "Motion ads support 1:1, 9:16, 16:9, 4:3, 3:4, and 21:9. Ask the agent for other sizes in the chat."
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["Sync your ad accounts in Context Hub's ", _jsx(_components.a, {
            href: "/context-hub/connections#connect-advertising-and-lifecycle-platforms",
            children: "Connected data"
          }), ". The agent uses video ads from your connected platforms as reference when generating new ones, and synced ads appear under ", _jsx(_components.a, {
            href: "/context-hub/assets",
            children: "Assets"
          }), " in the ", _jsx(_components.strong, {
            children: "Synced"
          }), " tab."]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Detailed prompting matters more for video than for statics. See ", _jsx(_components.a, {
          href: "/ad-studio/prompting-guide#create-motion-ads",
          children: "Create motion ads"
        }), " for templates."]
      }), "\n", _jsx(_components.h3, {
        id: "turn-a-long-video-into-multiple-ads",
        children: "Turn a long video into multiple ads"
      }), "\n", _jsx(_components.p, {
        children: "Attach a long video, such as a brand film, product demo, or customer story, and prompt the agent to cut it into standalone short ads. Ask for cuts on natural scene breaks and an end card on each clip, so every ad carries one message instead of reading as an excerpt."
      }), "\n", _jsxs(_components.p, {
        children: ["The agent creates a separate editable ad for each clip, so you review, resize, and launch them individually. See ", _jsx(_components.a, {
          href: "/ad-studio/prompting-guide#turn-a-long-video-into-multiple-ads",
          children: "Turn a long video into multiple ads"
        }), " for the prompt."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "organize-your-ads",
        children: "Organize your ads"
      }), "\n", _jsx(_components.p, {
        children: "Your ads live across every chat you create, so Ad Studio gives you a few ways to find and group them from the sidebar:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "All ads"
          }), " — Every ad in the workspace, across all chats."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "My ads"
          }), " — Ads from the chats you created."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "My favorites"
          }), " — Ads you've favorited with the heart icon in the ", _jsx(_components.a, {
            href: "/ad-studio/refine#favorite-queue-and-export-ads",
            children: "toolbar"
          }), ", gathered in one place."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Folders"
          }), " — Group related ads no matter which chat produced them. A folder can hold ads from multiple chats, and its own ", _jsx(_components.strong, {
            children: "Launch"
          }), " tab lets you launch all of a folder's ads together."]
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "whats-next",
        children: "What's next"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/ad-studio/refine",
            children: "Refine and collaborate: Iterate in the chat or visual editor, comment, and add formats"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/ad-studio/launch",
            children: "Launch ads: Queue finished ads and send them to your ad platforms"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/ad-studio/prompting-guide",
            children: "Prompting guide: What to put in a prompt, and templates for every format"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/ad-studio/insights",
            children: "Insights: Find fatiguing creatives and competitor activity"
          })
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
